import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const SITE_URL = 'https://mailpercent.co.za'
const SITE_NAME = 'Mail Percent'

type SeoPage = {
  title: string
  description: string
  canonicalPath: string
}

const PUBLIC_PAGES: Record<string, SeoPage> = {
  '/': {
    title: 'Mail Percent | Customer Email Response & Team Accountability',
    description: 'See customer email response gaps, queue pressure, team workload and follow-through across Gmail and Outlook. Mail Percent helps customer-facing teams act before opportunities go cold.',
    canonicalPath: '/',
  },
  '/about': {
    title: 'About Mail Percent | Customer Email Operations',
    description: 'Learn how Mail Percent makes customer email operations measurable, helping organisations understand response timing, workload, queue pressure and team follow-through.',
    canonicalPath: '/about',
  },
  '/product': {
    title: 'Customer Email Operations Software | Mail Percent',
    description: 'Measure customer email response timing, queue pressure, SLA Performance, follow-through and team workload without replacing Gmail or Outlook.',
    canonicalPath: '/product',
  },
  '/how-it-works': {
    title: 'How Mail Percent Measures Customer Email Response Work',
    description: 'See how Mail Percent uses organisation-authorised Gmail and Microsoft 365 metadata to measure customer email response times, queue pressure and SLA Performance.',
    canonicalPath: '/how-it-works',
  },
  '/teams': {
    title: 'Email Accountability for Customer-Facing Teams | Mail Percent',
    description: 'Give sales, support, customer success and account teams clearer visibility into customer email workload, response standards and follow-through.',
    canonicalPath: '/teams',
  },
  '/integrations': {
    title: 'Gmail & Outlook Integrations | Mail Percent',
    description: 'Connect Mail Percent to Gmail or Outlook and measure customer email operations while your team continues working in the inbox it already uses.',
    canonicalPath: '/integrations',
  },
  '/security': {
    title: 'Security & Privacy | Mail Percent',
    description: 'Learn how Mail Percent is designed around operational email events and metadata while limiting collection of customer email content.',
    canonicalPath: '/security',
  },
  '/pricing': {
    title: 'Mail Percent Pricing | Customer Email Operations',
    description: 'Mail Percent costs R245 per measured user per month. Viewer-only managers are free, with a 14-day free trial and volume pricing for teams of 50 or more.',
    canonicalPath: '/pricing',
  },
  '/contact': {
    title: 'Contact & Support | Mail Percent',
    description: 'Contact Mail Percent for workspace support, Google Workspace or Microsoft 365 integration help, connected mailbox questions, privacy requests and account assistance.',
    canonicalPath: '/contact',
  },
  '/privacy': {
    title: 'Privacy Policy | Mail Percent',
    description: 'Read how Mail Percent handles account information, connected mailbox data, Google user data and operational email-work measurements.',
    canonicalPath: '/privacy',
  },
  '/terms': {
    title: 'Terms of Service | Mail Percent',
    description: 'Read the terms governing Mail Percent accounts, workspaces, connected email services, operational measurements and acceptable use.',
    canonicalPath: '/terms',
  },
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)

  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value)
  }
}

function upsertCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!element) {
    element = document.createElement('link')
    element.rel = 'canonical'
    document.head.appendChild(element)
  }

  element.href = href
}

function updateStructuredData(show: boolean) {
  const existing = document.getElementById('mail-percent-structured-data')

  if (!show) {
    existing?.remove()
    return
  }

  const script = (existing ?? document.createElement('script')) as HTMLScriptElement
  script.id = 'mail-percent-structured-data'
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/images/brand/mail-percent-logo.png`,
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}/#software`,
        name: SITE_NAME,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: SITE_URL,
        description: 'Customer email operations software for measuring response timing, queue pressure, workload and team follow-through across Gmail and Outlook.',
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
    ],
  })

  if (!existing) document.head.appendChild(script)
}

export function SeoMetadata() {
  const location = useLocation()

  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/'
    const page = PUBLIC_PAGES[path]
    const isPublicPage = Boolean(page)

    const title = page?.title ?? 'Mail Percent | Workspace'
    const description = page?.description ?? 'Mail Percent workspace.'

    document.title = title

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: description,
    })

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: isPublicPage ? 'index, follow' : 'noindex, nofollow',
    })

    upsertMeta('meta[name="googlebot"]', {
      name: 'googlebot',
      content: isPublicPage
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, nofollow',
    })

    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    })

    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: SITE_NAME,
    })

    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    })

    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    })

    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary',
    })

    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: title,
    })

    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    })

    if (page) {
      const canonical = `${SITE_URL}${page.canonicalPath === '/' ? '/' : page.canonicalPath}`
      upsertCanonical(canonical)

      upsertMeta('meta[property="og:url"]', {
        property: 'og:url',
        content: canonical,
      })
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove()
      document.head.querySelector('meta[property="og:url"]')?.remove()
    }

    updateStructuredData(path === '/')
  }, [location.pathname])

  return null
}
